import React from 'react';
import {useSelector} from 'react-redux';

import {Alert} from '../../components/Alert';
import {Alerts, interpolate} from '../../constants/Strings';
import {colors} from '../../styles';
import {moodlightApi} from '../api/apis';
import {
  allAndCurrentDeviceMoodLightsSelector,
  deleteMoodLight as deleteAccountMoodLight,
} from '../redux/slices/moodLightSlice';
import {
  heatProfilesListSelector,
  upsertAccountHeatProfiles,
} from '../redux/slices/profilesSlice';
import {useAppDispatch} from '../redux/useAppDispatch';
import {HeatProfile, MoodLight, isMoodLightId} from '../types';
import {useAsyncFn} from './useAsyncFn';

interface DeleteMoodLightOptions {
  id: string;
  onDelete?: () => void;
}
const heatProfilesThatHaveMoodLightAssigned =
  (id: string) => (profile: HeatProfile) =>
    isMoodLightId(profile.light) && profile.light.id === id;

export const useDeleteMoodLight = () => {
  const dispatch = useAppDispatch();
  const profiles = useSelector(heatProfilesListSelector);
  const moodLights = useSelector(allAndCurrentDeviceMoodLightsSelector);

  const unassignFromHeatProfiles = async (moodLight: MoodLight) => {
    const updated = profiles
      .filter(
        profile =>
          isMoodLightId(profile.light) && profile.light.id === moodLight.id,
      )
      .map(profile => ({...profile, light: {color: colors.defaultColor}}));

    dispatch(upsertAccountHeatProfiles(updated));
  };

  const [state, onConfirmPress] = useAsyncFn(async (moodLight: MoodLight) => {
    await moodlightApi
      .deleteMoodLight({moodLightId: moodLight.id})
      .catch(error => {
        Alert.alert(Alerts.MOOD_DELETE_ERROR_TITLE, (error as Error).message);

        throw error;
      });

    dispatch(deleteAccountMoodLight(moodLight.id));

    await unassignFromHeatProfiles(moodLight);
  }, []);

  const deleteMoodLight = React.useCallback(
    ({id, onDelete}: DeleteMoodLightOptions) => {
      const moodLight = moodLights[id];

      if (!moodLight) return;

      const matching = profiles.filter(
        heatProfilesThatHaveMoodLightAssigned(id),
      );

      const allNames = Array.from(new Set(matching.map(({name}) => name))).join(
        ', ',
      );

      Alert.alert(
        Alerts.CONFIRM_DELETION,
        matching.length
          ? interpolate(Alerts.CONFIRM_WHITEN_PROFILES, {
              allNames,
            })
          : interpolate(Alerts.CONFIRM_DELETE_MOOD_LIGHT, {
              moodLight: moodLight?.name ?? 'this mood light',
            }),
        [
          {text: 'Cancel'},
          {
            text: 'OK',
            onPress: () =>
              onConfirmPress(moodLight).then(() => {
                onDelete?.();
              }),
          },
        ],
      );
    },
    [profiles, moodLights, onConfirmPress],
  );

  return [state, deleteMoodLight] as const;
};
